import { useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { PublicHeader } from '../components/PublicHeader';
import { PublicFooter } from '../components/PublicFooter';
import { usePublicEvents } from '../hooks/usePublicEvents';
import { isEventLive } from '../utils/eventUtils';
import { setPublicAreaActive } from '@/lib/analytics';
import { useSiteVisitCounter } from '../hooks/useSiteVisitCounter';
import '../styles/public.css';

// Só o Provider aqui — nenhum <Helmet> próprio neste nível. Ter um
// <Helmet> "default" no layout ao mesmo tempo que a página (via <Outlet>)
// renderiza o seu quebra o meta[name=description] (react-helmet-async não
// deduplica meta tags entre duas instâncias de <Helmet> simultâneas — só o
// <title> é tratado como nó único). Cada página resolve seu próprio
// título/descrição, com fallback pro texto da Home quando não tem nada
// específico ainda pronto (ver DEFAULT_SEO_TITLE/DESCRIPTION).

export function PublicLayout() {
  // Carregado aqui (não em HomePage) porque o dropdown de sugestões do
  // PublicHeader — visível em qualquer página pública, já que o header é
  // compartilhado — precisa dos mesmos eventos que a Home filtra. Uma
  // fonte única evita duas leituras redundantes do Firestore e garante que
  // clicar numa sugestão e apertar Enter na Home batam no mesmo dado.
  const { today, featured, upcoming, loading, error } = usePublicEvents();
  // today sempre vinha inteiro antes de upcoming aqui, então um evento ao
  // vivo (dentro de upcoming, já ordenado com prioridade lá em
  // publicEventService.js) podia ficar atrás de um evento de "today" que
  // ainda nem começou. Reordena o array final: ao vivo agora primeiro,
  // depois ordem cronológica — mesma checagem (isEventLive) que decide o
  // badge "Agora", só reaproveitada aqui.
  const combinedEvents = [...today, ...upcoming].sort((a, b) => {
    const liveA = isEventLive(a._raw);
    const liveB = isEventLive(b._raw);
    if (liveA !== liveB) return liveA ? -1 : 1;
    return (a._startDate?.getTime() ?? 0) - (b._startDate?.getTime() ?? 0);
  });

  // Termo de busca "confirmado" (Enter, ou campo vazio) — o que de fato
  // filtra a lista "Próximos Eventos" na Home. Vive aqui porque o campo de
  // busca (header) e a lista que ele filtra (Home) são componentes irmãos
  // sob este layout, não pai/filho.
  const [searchTerm, setSearchTerm] = useState('');

  // Analytics (GA4) só coleta enquanto a pessoa está na área pública — ver
  // lib/analytics.js. Sair pro admin/organizador/login desliga a coleta.
  useEffect(() => {
    setPublicAreaActive(true);
    return () => setPublicAreaActive(false);
  }, []);

  // Visita anônima ao site (1x por aba), sem consentimento: não usa cookie nem
  // identificador — ver hooks/useSiteVisitCounter.js.
  useSiteVisitCounter();

  // Com React 19 o <SeoHead> (react-helmet-async v3) renderiza um <title>
  // nativo a mais, sem substituir o estático do public/index.html — ficavam
  // dois <title> no documento, e a aba mostrava o primeiro (o genérico).
  // Aqui o título é sempre resolvido por página, então o estático sai.
  useEffect(() => {
    document.querySelector('title[data-static-title]')?.remove();
  }, []);

  return (
    <HelmetProvider>
      <div className="min-h-screen flex flex-col bg-background text-foreground">
        <PublicHeader events={combinedEvents} onSearch={setSearchTerm} />
        <main className="flex-1">
          <Outlet context={{ today, featured, upcoming, combinedEvents, loading, error, searchTerm }} />
        </main>
        <PublicFooter />
      </div>
    </HelmetProvider>
  );
}
